// src/components/EdgeCaptureImage.tsx
// Shared component for displaying a capture image fetched from the Edge.
// Falls back to an "Available only On Site per Regulations" placeholder
// when the image can't be loaded (Guard off-network, file gone, etc.).
//
// Extracted from the (now-removed) POIGalleryManager so any future
// capture-image consumer can share the same render path and on-site-only
// placeholder. Currently unused but kept as a building block.
//
// v0.4.9.1.13: prefer hostname (HTTPS) over IP (HTTP) for Edge URLs.
//
// v0.4.9.1.14: HTTPS → HTTP fallback chain (2 stages).
//
// v0.4.9.1.15: 3-stage ladder. v0.4.9.1.14 only tried hostname then HTTP IP;
// if DNS didn't resolve the hostname OR the cert SAN didn't match the
// hostname, the <img> silently failed without trying the IP. We now walk:
//   1. primary = https://${hostname}/captures/:id        (wildcard cert SAN match)
//   2. onError → swap to https://${ip}/captures/:id      (cert IP SAN match — works without DNS)
//   3. onError → swap to http://${ip}:${port}/captures/:id (cleartext last resort)
//   4. onError → render the OnSiteOnlyPlaceholder
// Only after ALL FOUR attempts fail do we treat the capture as truly
// off-network — matching the user's "Available only on Site per Regulations"
// rule.
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

// Placeholder shown when the capture image can't be loaded.
export function OnSiteOnlyPlaceholder({ size = 'normal' }: { size?: 'small' | 'normal' | 'large' }) {
  const sizeClass =
    size === 'small' ? 'w-20 h-20 text-[10px]' :
    size === 'large' ? 'w-48 h-48 text-sm' :
    'w-32 h-32 text-xs';
  return (
    <div
      className={`${sizeClass} bg-black dark:bg-gray-700 text-gray-300 dark:text-gray-400 rounded flex flex-col items-center justify-center text-center px-2 border border-gray-700`}
      title="The image is only available on-site per regulations."
    >
      <AlertCircle className="w-4 h-4 mb-1 opacity-60" />
      <span>Available only</span>
      <span>On Site per</span>
      <span>Regulations</span>
    </div>
  );
}

// Image that walks a 3-stage HTTPS→HTTPS→HTTP→placeholder ladder on successive errors.
export function EdgeCaptureImage({
  edgeIp,
  edgePort,
  edgeHostname,
  captureId,
  alt,
  size = 'normal',
}: {
  edgeIp?: string;
  edgePort?: number;
  edgeHostname?: string;
  captureId: number;
  alt: string;
  size?: 'small' | 'normal' | 'large';
}) {
  // v0.4.9.1.15: 4-stage ladder (0–3) for the onError chain.
  //   0 — show primary  (HTTPS via hostname)
  //   1 — swap to ipHttps (HTTPS via IP — cert IP SAN match)
  //   2 — swap to fallback (HTTP via IP:port — cleartext last resort)
  //   3 — show placeholder (all failed)
  const [stage, setStage] = useState<0 | 1 | 2 | 3>(0);

  const hostname = edgeHostname && edgeHostname.trim() !== '';
  const hasIp = !!edgeIp;
  const hasIpPort = !!(edgeIp && edgePort);

  // Build the three URL stages. Null = no candidate for that stage.
  // Stage 0 (primary): prefer hostname HTTPS, fall back to IP HTTPS if no hostname.
  const primary = hostname
    ? `https://${hostname}/captures/${captureId}`
    : (hasIp ? `https://${edgeIp}/captures/${captureId}` : null);

  // Stage 1 (ipHttps): only meaningful if we used hostname for primary,
  // or if hostname === ipHttps (degenerate case) — skip the swap.
  let ipHttps: string | null = null;
  if (hostname && hasIp) {
    ipHttps = `https://${edgeIp}/captures/${captureId}`;
  }

  // Stage 2 (fallback): cleartext HTTP via IP:port.
  const fallback = hasIpPort ? `http://${edgeIp}:${edgePort}/captures/${captureId}` : null;

  // No endpoints at all → placeholder immediately.
  if (!primary) {
    return <OnSiteOnlyPlaceholder size={size} />;
  }

  // If ipHttps equals primary (no hostname, only IP), don't ladder through it.
  // If fallback equals primary or ipHttps (degenerate), skip it.
  const effectiveIpHttps = (ipHttps && ipHttps !== primary) ? ipHttps : null;
  const effectiveFallback =
    (fallback && fallback !== primary && fallback !== effectiveIpHttps) ? fallback : null;

  // All stages exhausted → show placeholder.
  if (stage === 3) {
    return <OnSiteOnlyPlaceholder size={size} />;
  }

  // Pick the URL for the current stage.
  let currentSrc: string;
  if (stage === 0) currentSrc = primary;
  else if (stage === 1) currentSrc = effectiveIpHttps || effectiveFallback || primary;
  else currentSrc = effectiveFallback || primary;

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={`${size === 'small' ? 'w-20 h-20' : size === 'large' ? 'w-48 h-48' : 'w-32 h-32'} object-cover rounded`}
      onError={() => {
        // v0.4.9.1.15: walk the 3-stage ladder. Use functional update so we
        // always read the latest stage, not a stale closure. When the next
        // stage is null (nothing to try), jump straight to placeholder.
        setStage((prev) => {
          if (prev === 0) return effectiveIpHttps ? 1 : (effectiveFallback ? 2 : 3);
          if (prev === 1) return effectiveFallback ? 2 : 3;
          return 3;
        });
      }}
    />
  );
}
